import { Grid, TextField, TextFieldProps } from "@mui/material";

import {
  Control,
  Controller,
  ControllerProps,
  useFormContext,
} from "react-hook-form";
import React from "react";
import CustomTooltip from "./customTooltip";

export type CustomNumberFieldProps = Omit<TextFieldProps, "name"> & {
  validation?: ControllerProps["rules"];
  name: string;
  toolTipTitle?: string;
  control?: Control<any>;
  readOnly?: boolean;
  autoComplete?: string;
  length: number;
  TypedLength?: boolean;
  label?: string;
  onCustomCallback?: (value: any) => void;
};

export default function CustomNumberField({
  validation = {},
  type,
  required,
  name,
  toolTipTitle,
  readOnly,
  autoComplete,
  length,
  control,
  TypedLength,
  label,
  onCustomCallback,
  ...rest
}: CustomNumberFieldProps): JSX.Element {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={validation}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <Grid container alignItems="center">
          <Grid item xs={12}>
            <Grid container alignItems="center">
              <Grid item xs={12}>
                <Grid container alignItems="center">
                  <Grid
                    item
                    xs={12}
                    sx={{
                      fontSize: "14px",
                      fontFamily: "Poppins",
                      fontWeight: "300",
                      mb: "4px",
                      ml: "4px",
                      mr: "6px",
                      color: "#797979",
                    }}
                  >
                    {label}
                  </Grid>
                  {toolTipTitle && <CustomTooltip title={toolTipTitle} />}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <TextField                  
                  fullWidth
                  {...rest}
                  value={value || ""}
                  required={required}
                  type={type}
                  {...register(name)}
                  inputProps={{
                    maxLength: length ? length : null,
                    readOnly: readOnly ? readOnly : false,
                    TypedLength: TypedLength ? TypedLength : false,
                    autoComplete: autoComplete ? autoComplete : null,
                  }}
                  sx={{
                    borderRadius: "100px",
                    backgroundColor: "#fff",
                    "& .MuiInputLabel-root": {
                      color: "#cccccc", // Label color
                    },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "100px",
                      fontSize: "16px",
                      fontWeight: "200",
                      "& fieldset": {
                        borderRadius: "100px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#CCCCCC", // Hovered border color
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#CCCCCC", // Focused border color
                      },
                      "& input": {
                        color: "#000000", // Text color inside input
                      },
                    },
                    "& input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px transparent inset",
                      WebkitTextFillColor: "#000000",
                      transition: "background-color 5000s ease-in-out 0s",
                    },
                  }}
                  error={!!error}
                  helperText={error ? error.message : rest?.helperText}
                  onChange={(event) => {
                    let item: number | string = event.target.value;

                    const reg = /^-?[0-9]*(\.[0-9]*)?$/;
                    if (
                      (!isNaN(Number(item)) && reg.test(item)) ||
                      item === "" ||
                      item === "-"
                    ) {
                      onChange(item);
                    }
                    if (typeof onCustomCallback === "function") {
                      let item = event.target.value;
                      onCustomCallback(item);
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    />
  );
}
